import { Facet, IFaceted } from '../models/Facet';
import { KeyFacet, stringPart } from '../models/KeyFacet';
import { IQueuedOperation } from '../store/reducers/operationMapReducer';

/**
 * @internal
 */
export const supportedFacetsItemKeyFacet = new KeyFacet<{
  keyFacetId: string;
}>('supportedFacets', {
  keyFacetId: stringPart()
});

/**
 * @internal
 */
export const supportedFacetsFacet = new Facet<{
  demands: IFaceted<boolean>;
}>('supportedFacets');

/**
 * Facet which marks on an item which KeyFacet is the preferred form to use for the item.
 * Operations will attempt to use the preferred key facet, if provided, falling
 * back to the key they originally started with, when delegating to addons for processing.
 *
 * Supply this facet to an item to ensure that the best addon is used to provide
 * information for a given item.
 */
export const preferredKeyFacetFacet: Facet<KeyFacet<{}>> = new Facet('preferredKeyFacet');

/**
 * Facet tracking the queue of operations for an operation type.
 * This facet should be applied to an `itemKey` from `OperationTypeKeyFacet`.
 *
 * @internal
 */
export const operationQueueOperationsFacet: Facet<IQueuedOperation<object, object>[]> = new Facet('operationQueueOperations');

/**
 * The handler registered for an operation queue.
 * This facet should be applied to an `itemKey` from `OperationTypeKeyFacet`.
 *
 * @internal
 */
export const operationQueueHandlerFacet: Facet<() => void> = new Facet('operationQueueHandler');

/**
 * Facet which tracks whether or not an operation handler is registered for a queue.
 * This facet should be applied to an `itemKey` from `OperationTypeKeyFacet`.
 *
 * @internal
 */
export const isOperationHandlerRegisteredFacet: Facet<boolean> = new Facet('isOperationHandlerRegistered');

/**
 * Options for configuration operation handlers globally.
 * Individual operation handlers may override these defaults.
 *
 * @internal
 */
export interface IOperationHandlerConfiguration {
  /**
   * Overrides the default timeout (milliseconds) for operation handlers. The original default is 1 minute.
   */
  defaultTimeout?: number;
  /**
   * Enables use of the default timeout.
   */
  enableTimeout?: boolean;
}

/**
 * Configuration facet which specifies some default overall behaviors for operation handlers.
 *
 * @internal
 */
export const operationHandlerConfigurationFacet: Facet<IOperationHandlerConfiguration> = new Facet('operationHandlerConfiguration');
